<template>
    <div class="Field" v-if="namespace && hydrated">
        <TextInput v-if="type === 'text'"
                   :field-id="fieldId"
                   :field-namespace="namespace"
        />

        <Paragraph v-else-if="type === 'textarea'"
                   :field-id="fieldId"
                   :field-namespace="namespace"
        />

        <WYSIWYG v-else-if="type === 'wysiwyg'"
                 :field-id="fieldId"
                 :field-namespace="namespace"
        />

        <Address v-else-if="type === 'address'"
                 :field-id="fieldId"
                 :field-namespace="namespace"
        />

        <ImageSelector v-else-if="type === 'image-upload'"
                       :field-id="fieldId"
                       :field-namespace="namespace"
                       :load-user-library="true"
                       :user-media="userMedia"
                       :load-stock-library="loadStock"
                       :stock-media="stockMedia"
                       selected-image-id="0"
                       :field-location="card + '/' + name"
                       :allow-upload="true"
                       :upload-type="uploadType"
        />

        <PaletteDesigner v-else-if="type === 'color-select'"
                         :field-id="fieldId"
                         :field-namespace="namespace"
                         :card-namespace="cardNamespace"
        />

        <FontDesigner v-else-if="type === 'font-select'"
                      :field-id="fieldId"
                      :field-namespace="namespace"
                      :card-namespace="cardNamespace"
        />

        <SitePreview v-else-if="type === 'site-preview'"
                     :field-id="fieldId"
                     :field-namespace="namespace"
        />

        <TemplateSelector v-else-if="type === 'template-select'"
                          :field-id="fieldId"
                          :field-namespace="namespace"
                          :card-namespace="cardNamespace"
        />

        <TrainingField v-else-if="type === 'training'"
                       :field-id="fieldId"
                       :field-namespace="namespace"
                       :card-namespace="cardNamespace"
        />

        <Schedule v-else-if="type === 'schedule'"
                  :field-id="fieldId"
                  :field-namespace="namespace"
                  :card-namespace="cardNamespace"
        />

        <FieldGroup v-else-if="type === 'group'"
                    :field-id="fieldId"
                    :field-namespace="namespace"
                    :card-namespace="cardNamespace"
        />

        <StaticText v-else-if="type ==='static-text'"
                    :namespace="namespace"
        />

        <AudioPlayer v-else-if="type ==='audio-player'"
                     :namespace="namespace"
        />

        <Testimonial v-else-if="type === 'testimonial'"
                     :field-id="fieldId"
                     :field-namespace="namespace"
                     :card-namespace="cardNamespace"
        />

        <HeroSelector v-else-if="type === 'hero-selector'"
                      :field-id="fieldId"
                      :field-namespace="namespace"
                      :card-namespace="cardNamespace"
        />

        <span v-else> Error: {{ name }} </span>
    </div>
</template>

<script>
import FieldModule from '@/store/Modules/Field/User/field.store';
import store from '../../../store/store';
import TextInput from './Fields/Text';
import Paragraph from './Fields/Paragraph';
import FieldGroup from '@/views/Public/Field/Fields/Group';
import ImageSelector from '@/components/ImageSelector/ImageSelector';
import TrainingField from '@/views/Public/Field/Fields/TrainingField';
import PaletteDesigner from '@/views/Public/Field/Fields/PaletteDesigner';
import FontDesigner from '@/views/Public/Field/Fields/FontDesigner';
import SitePreview from '@/views/Public/Field/Fields/SitePreview';
import Address from '@/views/Public/Field/Fields/Address';
import WYSIWYG from '@/views/Public/Field/Fields/WYSIWYG';
import Testimonial from '@/views/Public/Field/Fields/Testimonial';
import HeroSelector from '@/views/Public/Field/Fields/HeroSelector';
import TemplateSelector from '@/views/Public/Field/Fields/TemplateSelector';
import Schedule from '@/views/Public/Field/Fields/Schedule';
import AudioPlayer from '@/views/Public/Field/Fields/AudioPlayer';
import StaticText from '@/views/Public/Field/Fields/StaticText';

export default {
    components: {
        StaticText,
        AudioPlayer,
        Schedule,
        TemplateSelector,
        HeroSelector,
        Testimonial,
        WYSIWYG,
        Address,
        SitePreview,
        FontDesigner,
        PaletteDesigner,
        TrainingField,
        ImageSelector,
        FieldGroup,
        TextInput,
        Paragraph
    },
    props: {
        fieldId: {
            type: String,
            required: true
        },

        cardNamespace: {
            type: String,
            required: true
        }
    },

    computed: {
        type() {
            return store.state[this.namespace].type;
        },

        hydrated() {
            return store.state[this.namespace].hydrated;
        },

        name() {
            return store.state[this.namespace].name;
        },

        card() {
            return store.state[this.namespace].card;
        },

        section() {
            return store.state[this.namespace].section;
        },

        step() {
            return store.state[this.namespace].step;
        },

        stockMedia() {
            return store.state[this.namespace].stockMedia;
        },

        uploadType() {
            return store.state[this.namespace].options.uploadType;
        },

        userMedia() {
            return store.state[this.namespace].userMedia;
        },

        /**
         * Options
         * @returns {any}
         */

        loadStock() {
            return store.state[this.namespace].options.loadStock;
        },
    },

    data() {
        return {
            namespace: ''
        };
    },

    created() {
        if (this.fieldId) {
            this.registerStoreModule()
                .then((namespace) => {
                    this.loadData(namespace);
                });
        }
    },

    beforeDestroy() {
        store.unregisterModule(this.namespace);
    },

    methods: {
        loadData(ns) {
            return new Promise((resolve, reject) => {
                store.dispatch(`${ns}/getData`, this.fieldId)
                    .then(() => {
                        store.commit(`${this.cardNamespace}/addLoadedField`, this.fieldId);
                        this.namespace = ns;
                        resolve();
                    })
                    .catch(() => {
                        reject();
                    });
            });
        },

        registerStoreModule() {
            return new Promise((resolve) => {
                const ns = `field-user-${this.fieldId}`;

                if ((store && store.state && store.state[ns])) {
                    store.unregisterModule(ns);
                }

                store.registerModule(ns, FieldModule);
                resolve(ns);
            });
        }
    }
};
</script>
<style lang="scss">
.PaletteSelector {
    flex: 0 0 100%;
    width: 100%;
    margin-bottom: 36px;
}

.FontSelector {
    flex: 0 0 100%;
    width: 100%;
    margin-bottom: 36px;
}

.SitePreview {
    margin-bottom: 36px;
}

.ExpansionCard__actions--design {
    margin-top: 12px;
}

.Field {
    display: flex;
    flex-flow: row wrap;
    flex: 0 0 100%;
    width: 100%;
}

.Field + * {
    margin-top: 12px;
}

.FieldHelpWrap {
    display: flex;
    flex-flow: row nowrap;
    justify-content: space-between;
    flex: 0 0 100%;
    width: 100%;
}

.FieldHelpWrap__input {
    flex: 1 1 80%;
    width: 80%
}

.FieldHelpWrap__help {
    display: flex;
    flex-flow: row nowrap;
    justify-content: flex-end;
    align-content: center;
    align-items: center;
    flex: 0 0 36px;
    width: 36px;
    height: 56px;
}

.jsReadOnly {
    .v-input__slot {
        background-color: #eaeaea;
    }
}
</style>
